import * as React from 'react';
import propz from 'propz';
import { LIMIT } from 'Src/consts/table';
import { ColumnDefinition } from 'Src/helpers/table/table';
import { FILTER_TYPE } from 'Src/consts/table';
import { DatePicker as SitDatePicker } from 'Src/components/DatePicker/DatePicker';
import moment from 'moment';
import { AppUser } from 'Src/views/App/App';
import { loadHiddenColumns, saveHiddenColumns } from 'Src/helpers/columnVisibility/columnVisibility';
import { SelectAllCheckbox } from 'Src/components/SelectAllCheckbox/SelectAllCheckbox';

interface Props {
  title: string;
  user: AppUser;
  viewKey: string;
  items: any[];
  columns: ColumnDefinition[];
  selectedItems: any[];
  currentPage: number;
  total: number;
  filters: any;
  isShowFilter: boolean;
  isDataFiltered: boolean;
  filterOptions: { [field: string]: any[] };
  actions: any[];
  onItemClick: (index: number) => void;
  onOpenItem: (index: number) => void;
  onFilterToggle: (event: any) => void;
  onFilterChange: (event: any, field: string, options?: any) => void;
  onApplyFilter: () => void;
  onClearFilter: () => void;
  onRemoveFilter: (field: string) => void;
  onCancelFilter: () => void;
  onSelectAllPage: () => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
  onPage: (page: number) => void;
  requiresFilters?: boolean;
  filterHint?: string;
}

const getValue = (item: any, column: ColumnDefinition): React.ReactNode => {
  const accessor: any = (column as any).accessor;
  if (typeof accessor === 'function') return accessor(item);
  if (Array.isArray(accessor)) return propz.get(item, accessor, '—');
  return propz.get(item, [column.field], '—');
};

export const MessagesListView = (props: Props) => {
  const availableColumns = props.columns.filter(column => column.field !== 'messageTakePart').slice(0, 6);
  const fixedColumnField = availableColumns.length > 0 ? availableColumns[0].field : '';
  const [isColumnsOpen, setIsColumnsOpen] = React.useState(false);
  const [hiddenColumns, setHiddenColumns] = React.useState<Set<string>>(
    () =>
      new Set(
        loadHiddenColumns(props.user, props.viewKey).filter(
          field => field !== fixedColumnField && availableColumns.some(column => column.field === field)
        )
      )
  );
  const columnsRef = React.useRef<HTMLDivElement>(null);
  const filtersRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!isColumnsOpen) return undefined;
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (columnsRef.current && !columnsRef.current.contains(event.target as Node)) setIsColumnsOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, [isColumnsOpen]);
  React.useEffect(() => {
    if (!props.isShowFilter) return undefined;
    const closeOnOutsideClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target.closest('.sit-dp__pop') || target.closest('.sit-cal')) return;
      if (filtersRef.current && !filtersRef.current.contains(target)) props.onCancelFilter();
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') props.onCancelFilter();
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [props.isShowFilter, props.onCancelFilter]);
  const selectedIds = new Set(props.selectedItems.map(item => item.id));
  const allPageSelected = props.items.length > 0 && props.items.every(item => selectedIds.has(item.id));
  const pageCount = Math.max(1, Math.ceil(props.total / LIMIT));
  const pageStart = Math.max(1, Math.min(props.currentPage - 2, pageCount - 4));
  const pageEnd = Math.min(pageCount, pageStart + 4);
  const pages = Array.from({ length: pageEnd - pageStart + 1 }, (_, index) => pageStart + index);
  const visibleColumns = availableColumns.filter(column => !hiddenColumns.has(column.field));
  const filterColumns = props.columns.filter(column => column.type && column.type !== FILTER_TYPE.NONE);
  const activeFilterColumns = filterColumns.filter(column => {
    const value = props.filters[column.field];
    return Array.isArray(value) ? value.length > 0 : value && (typeof value !== 'object' || value.from || value.to);
  });
  const setAndSaveHiddenColumns = (next: Set<string>) => {
    setHiddenColumns(next);
    saveHiddenColumns(props.user, props.viewKey, Array.from(next));
  };
  const getActiveFilterValue = (column: ColumnDefinition): string => {
    const value = props.filters[column.field];
    if (Array.isArray(value)) {
      return value
        .map(selectedValue => {
          const option = (props.filterOptions[column.field] || []).find(
            item => String(item.value) === String(selectedValue)
          );
          return option ? option.text : selectedValue;
        })
        .join(', ');
    }
    if (value && typeof value === 'object') return [value.from, value.to].filter(Boolean).join(' – ');
    const option = (props.filterOptions[column.field] || []).find(item => String(item.value) === String(value));
    return String(option ? option.text : value || '');
  };

  const renderFilterField = (column: ColumnDefinition) => {
    const value = props.filters[column.field];
    if (column.type === FILTER_TYPE.MULTISELECT) {
      const selectedValues = Array.isArray(value) ? value.map(String) : value ? [String(value)] : [];
      return (
        <fieldset key={column.field} className="messagesList__chipFilter">
          <legend>{column.text}</legend>
          <div>
            {(props.filterOptions[column.field] || []).map(option => {
              const optionValue = String(option.value);
              const isSelected = selectedValues.includes(optionValue);
              return (
                <button
                  key={optionValue}
                  type="button"
                  className={isSelected ? 'is-selected' : ''}
                  aria-pressed={isSelected}
                  onKeyDown={event => {
                    if (event.key === 'Enter' || event.key === ' ') event.stopPropagation();
                  }}
                  onClick={() => {
                    const next = isSelected
                      ? selectedValues.filter(item => item !== optionValue)
                      : [...selectedValues, optionValue];
                    props.onFilterChange({ target: { value: '' } }, column.field, { chipValues: next });
                  }}
                >
                  {option.text}
                </button>
              );
            })}
          </div>
        </fieldset>
      );
    }
    if (column.type === FILTER_TYPE.SELECT) {
      return (
        <label key={column.field}>
          <span>{column.text}</span>
          <select value={value || ''} onChange={event => props.onFilterChange(event, column.field)}>
            <option value="">All</option>
            {(props.filterOptions[column.field] || []).map(option => (
              <option key={String(option.value)} value={option.value}>
                {option.text}
              </option>
            ))}
          </select>
        </label>
      );
    }
    if (column.type === FILTER_TYPE.DATE_INTERVAL) {
      const range = value || {};
      return (
        <SitDatePicker
          key={column.field}
          label={`${column.text} interval`}
          mode="range"
          value={{
            start: range.from ? moment(range.from, 'DD-MM-YYYY').toDate() : null,
            end: range.to ? moment(range.to, 'DD-MM-YYYY').toDate() : null
          }}
          onChange={(nextRange: any) => {
            props.onFilterChange({ target: { value: '' } }, column.field, {
              modernRange: {
                from: nextRange && nextRange.start ? moment(nextRange.start).format('DD-MM-YYYY') : '',
                to: nextRange && nextRange.end ? moment(nextRange.end).format('DD-MM-YYYY') : ''
              }
            });
          }}
          format="DD-MM-YYYY"
          numberOfMonths={2}
          clearable={true}
          syncRangeOnSelect={true}
        />
      );
    }
    return (
      <label key={column.field}>
        <span>{column.text}</span>
        <input value={value || ''} onChange={event => props.onFilterChange(event, column.field)} />
      </label>
    );
  };

  return (
    <div className="messagesList">
      <div className="messagesList__toolbar">
        <div className="messagesList__title">
          <strong>{props.title}</strong>
          <span>{props.total}</span>
        </div>
        <div className="messagesList__filterAnchor" ref={filtersRef}>
          <button
            type="button"
            className={`messagesList__filterButton ${props.isShowFilter || props.isDataFiltered ? 'is-active' : ''}`}
            onClick={props.onFilterToggle}
          >
            <i /> Filters
            {activeFilterColumns.length > 0 && <b>{activeFilterColumns.length}</b>}
          </button>
          {props.isShowFilter && (
            <div
              className="messagesList__filters"
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  if (!props.requiresFilters) props.onApplyFilter();
                }
              }}
            >
              <header>
                <strong>Filters</strong>
                <button type="button" onClick={props.onCancelFilter} aria-label="Close filters">
                  ×
                </button>
              </header>
              {props.filterHint && <p className="messagesList__filterHint">{props.filterHint}</p>}
              <div className="messagesList__filterFields">{filterColumns.map(renderFilterField)}</div>
              <footer>
                <button type="button" className="is-clear" onClick={props.onClearFilter}>
                  Clear all
                </button>
                <div>
                  <button type="button" onClick={props.onCancelFilter}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="is-apply"
                    onClick={props.onApplyFilter}
                    disabled={props.requiresFilters}
                  >
                    Apply
                  </button>
                </div>
              </footer>
            </div>
          )}
        </div>
        <div className="messagesList__columns" ref={columnsRef}>
          <button
            type="button"
            className={`messagesList__columnsButton ${isColumnsOpen ? 'is-active' : ''}`}
            onClick={() => setIsColumnsOpen(!isColumnsOpen)}
            aria-expanded={isColumnsOpen}
          >
            <i /> Columns <span>{visibleColumns.length}</span>
          </button>
          {isColumnsOpen && (
            <div className="messagesList__columnsMenu">
              <div className="messagesList__columnsHead">
                <strong>Show / hide columns</strong>
                <button type="button" onClick={() => setAndSaveHiddenColumns(new Set())}>
                  Reset
                </button>
              </div>
              {availableColumns.map(column => (
                <label key={column.field} className={column.field === fixedColumnField ? 'is-fixed' : ''}>
                  <input
                    type="checkbox"
                    checked={!hiddenColumns.has(column.field)}
                    disabled={column.field === fixedColumnField}
                    onChange={() => {
                      const next = new Set(hiddenColumns);
                      next.has(column.field) ? next.delete(column.field) : next.add(column.field);
                      setAndSaveHiddenColumns(next);
                    }}
                  />
                  <span>{column.text}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      {activeFilterColumns.length > 0 && (
        <div className="messagesList__activeFilters">
          {activeFilterColumns.map(column => (
            <span key={column.field}>
              <b>{column.text}:</b> {getActiveFilterValue(column)}
              <button
                type="button"
                onClick={() => props.onRemoveFilter(column.field)}
                aria-label={`Remove ${column.text} filter`}
              >
                ×
              </button>
            </span>
          ))}
          <button type="button" className="messagesList__clearFilters" onClick={props.onClearFilter}>
            Clear all
          </button>
        </div>
      )}

      {props.selectedItems.length > 0 && (
        <div className="messagesList__selection">
          <strong>{props.selectedItems.length} selected</strong>
          {props.actions
            .filter(action => action.isActive)
            .map(action => (
              <button type="button" className="messagesList__button" key={action.itemText} onClick={action.onItemClick}>
                {action.itemText}
              </button>
            ))}
          <button type="button" className="messagesList__clearSelection" onClick={props.onClearSelection}>
            Clear selection
          </button>
        </div>
      )}

      <div className="messagesList__tableCard">
        <div className="messagesList__tableScroll">
          <table className="messagesList__table">
            <thead>
              <tr>
                <th className="messagesList__check">
                  <SelectAllCheckbox
                    compact
                    isChecked={allPageSelected}
                    totalCount={props.total}
                    currentPage={props.currentPage}
                    pageItemsCount={props.items.length}
                    selectedItems={props.selectedItems}
                    onSelectAllOnPageClick={props.onSelectAllPage}
                    onSelectAllClick={props.onSelectAll}
                    onUnselectAllClick={props.onClearSelection}
                  />
                </th>
                {visibleColumns.map(column => (
                  <th key={column.field}>{column.text}</th>
                ))}
                <th className="messagesList__open" aria-label="Open message" />
              </tr>
            </thead>
            <tbody>
              {props.items.map((item, index) => (
                <tr
                  key={item.id || index}
                  className={selectedIds.has(item.id) ? 'is-selected' : ''}
                  onClick={() => props.onOpenItem(index)}
                >
                  <td className="messagesList__check" onClick={event => event.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(item.id)}
                      onChange={() => props.onItemClick(index)}
                    />
                  </td>
                  {visibleColumns.map(column => (
                    <td key={column.field}>{getValue(item, column)}</td>
                  ))}
                  <td className="messagesList__open" aria-hidden="true">
                    <span>›</span>
                  </td>
                </tr>
              ))}
              {props.items.length === 0 && (
                <tr>
                  <td className="messagesList__empty" colSpan={visibleColumns.length + 2}>
                    <div>
                      <span aria-hidden="true">
                        <svg viewBox="0 0 24 24">
                          <rect x="3.5" y="5" width="17" height="14" rx="2.5" />
                          <path d="m5 7 7 5 7-5" />
                        </svg>
                      </span>
                      <strong>
                        {props.requiresFilters
                          ? 'Choose filters to load the archive'
                          : props.isDataFiltered
                          ? 'No messages found'
                          : 'No messages yet'}
                      </strong>
                      <p>
                        {props.requiresFilters
                          ? 'Select at least one message type and a complete date range.'
                          : props.isDataFiltered
                          ? 'Try changing or clearing the applied filters.'
                          : `There are no messages in ${props.title.toLowerCase()}.`}
                      </p>
                      {props.requiresFilters && (
                        <button type="button" className="messagesList__emptyAction" onClick={props.onFilterToggle}>
                          Open filters
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {props.total > 0 && (
        <nav className="messagesList__pagination">
          <span>
            Page {props.currentPage} of {pageCount}
          </span>
          <button type="button" disabled={props.currentPage <= 1} onClick={() => props.onPage(props.currentPage - 1)}>
            Previous
          </button>
          {pageStart > 1 && (
            <>
              <button type="button" onClick={() => props.onPage(1)}>
                1
              </button>
              {pageStart > 2 && <i>…</i>}
            </>
          )}
          {pages.map(page => (
            <button
              type="button"
              key={page}
              className={page === props.currentPage ? 'is-active' : ''}
              onClick={() => props.onPage(page)}
            >
              {page}
            </button>
          ))}
          {pageEnd < pageCount && (
            <>
              {pageEnd < pageCount - 1 && <i>…</i>}
              <button type="button" onClick={() => props.onPage(pageCount)}>
                {pageCount}
              </button>
            </>
          )}
          <button
            type="button"
            disabled={props.currentPage >= pageCount}
            onClick={() => props.onPage(props.currentPage + 1)}
          >
            Next
          </button>
        </nav>
      )}
    </div>
  );
};
